import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { wh, fontSize, flexButton } from '../../mixins'
import { useSelector, useDispatch } from 'react-redux'
import { selectPlan, setPlan, selectLandingType, setDefaultPlanId, selectShowCheckout } from './plansSlice'


const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 0;
  margin: 35px 0 0 0;
  list-style: none;
`

const StyledLi = styled.li`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border: 1px solid ${({ active }) => (active ? '#3393EC' : 'rgba(0,0,0,.25)')};
  color: ${({ active }) => (active ? '#3393EC' : '#848484')};
  border-radius: 12px;
  ${wh('100%', '64px')};
  max-width: 327px;
  padding: 20px 16px;
  position: relative;
  cursor: pointer;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`

const StyledPeriod = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  justify-content: center;
  align-items: center;
  font-weight: 600;
  ${fontSize(14)};
  line-height: 20px;
  text-transform: uppercase;

  div {
    ${flexButton('24px')};
    min-width: 24px;
    border-radius: 50%;
    margin-right: 16px;
    margin-left: 16px;
    margin-${textAlign}: 0;
    background: ${({ active }) => (active ? '#3393EC' : '#fff')};
    border: 1px solid ${({ active }) => (active ? '#3393EC' : '#ADADAD')};
    transition: all 0.1s linear;
    transition-property: background-color, border-color;
  }
`

const StyledPrice = styled.p`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  position: relative;

  span {
    &:first-child {
      font-weight: 700;
      ${fontSize(16)};
      line-height: 16px;
      letter-spacing: -0.2px;
    }

    &.old-price {
      font-weight: 600;
      ${fontSize(12)};
      line-height: 16px;
      color: #848484;
      position: absolute;
      right: 55px;
      left: 43px;
      ${textAlign}: auto;

      &:after {
        content: '';
        display: block;
        ${wh('100%', '1px')};
        background: #ff7c7c;
        position: absolute;
        top: 50%;
        left: 0;
      }
    }

    &:last-child {
      font-weight: 600;
      ${fontSize(8)};
      line-height: 11px;
    }
  }
`

const BestLabel = styled.div`
  position: absolute;
  top: 0;
  right: 48px;
  left: 48px;
  ${textAlign}: auto;
  background: #3393ec;
  border-radius: 1000px;
  font-weight: 600;
  ${fontSize(10)};
  line-height: 14px;
  color: #fff;
  text-transform: uppercase;
  transform: translateY(-50%);
  padding: 3px 15px;
`

const Plans = () => {
  const { t } = useTranslation()
  const plan = useSelector(selectPlan)
  const dispatch = useDispatch()
  const landingType = useSelector(selectLandingType)
  const showCheckout = useSelector(selectShowCheckout)
  const defaultPaywall = useMemo(() => {
    return [
      {
        id: 'fl-3-month-garlic',
        title: '3',
        period: t('month'),
        price: '$2.99',
        week: t('per_week'),
        bestValue: true,
        oldPrice: '$9.99',
      },
      {
        id: 'fl-1-month-tomato',
        title: '1',
        period: t('month'),
        price: '$4.99',
        week: t('per_week'),
      },
      {
        id: 'fl-1-month-intro-franko',
        title: '1',
        period: `${t('week_trial')}`,
        price: '$9.99',
        week: `${t('per_week')}`,
      },
    ];
  },[t])

  const mainPaywall = useMemo(() => {
    return [
      {
        id: 'fl-monthly-3999',
        title: '1',
        period: t('month'),
        price: '$9.99',
        week: `${t('per_week')}`,
        bestValue: true,
      },
      {
        id: 'fl-3month-5999',
        title: '3',
        period: t('month'),
        price: '$4.99',
        week: t('per_week'),
      },
      {
        id: 'fl-6month-7176',
        title: '6',
        period: t('month'),
        price: '$2.99',
        week: t('per_week'),
      },
    ];
  },[t])

  const OFFERS = useMemo(() => {
    return {
      fullPrice: defaultPaywall,
      defb: defaultPaywall,
      anim: defaultPaywall,
      main : mainPaywall,
      'one-pager' : mainPaywall,
      map : mainPaywall,
      discountb: [
        {
          id: 'fl-monthly-free3d-2999',
          title: '3',
          period: `${t('3_days_free')}*`,
          label: t('popular'),
          bestValue: true,
        },
        {
          id: 'fl-1-month-tomato',
          title: '1',
          period: t('month'),
          price: '$4.99',
          week: t('per_week'),
        },
        {
          id: 'fl-3-month-garlic',
          title: '3',
          period: t('month'),
          price: '$2.99',
          week: t('per_week'),
        },
      ],
    }
  }, [t, defaultPaywall, mainPaywall])

  const currentPlans = useMemo(() => OFFERS[landingType], [landingType, OFFERS]);

  useEffect(() => {
    if (!showCheckout) {
      dispatch(setPlan(currentPlans[0].id))
      dispatch(setDefaultPlanId(currentPlans[0].id))
    }
  }, [dispatch, currentPlans, showCheckout])

  const onPlanClick = (id) => dispatch(setPlan(id))

  const renderOffer = (i) => {
    let isActive = plan === i.id

    return (
      <StyledLi active={isActive} onClick={() => onPlanClick(i.id)} key={i.id}>
        <StyledPeriod active={isActive}>
          <div>
            <svg
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.8243 0.186131C11.59 -0.0620437 11.2101 -0.0620437 10.9757 0.186131L3.78737 7.79935L1.02428 4.87298C0.789972 4.62481 0.410097 4.62483 0.175746 4.87298C-0.058582 5.12113 -0.058582 5.52346 0.175746 5.77163L3.3631 9.14729C3.59734 9.39544 3.97749 9.39526 4.21163 9.14729L11.8243 1.0848C12.0586 0.836653 12.0586 0.434306 11.8243 0.186131Z"
                fill="white"
              />
            </svg>
          </div>
          <span>
            {i.title} {i.period}
          </span>
        </StyledPeriod>
        <StyledPrice>
          <span>{i.price}</span>
          {i.oldPrice && <span className="old-price">{i.oldPrice}</span>}
          <span>{i.week}</span>
        </StyledPrice>
        {i.bestValue && <BestLabel>{t('most_popular')}</BestLabel>}
      </StyledLi>
    )
  }

  return <StyledList>{currentPlans.map(renderOffer)}</StyledList>
}

export default Plans
